'use client'

import { useEffect, useState } from 'react'
import Cookies from 'js-cookie'
import { useTranslation } from 'react-i18next'
import Link from 'next/link'
import { ChevronRightIcon } from '@heroicons/react/24/outline'

import { Currency } from '@/i18n'
import { Country } from '@/types'
import CountryFlag from '@/components/country-flag/country-flag'
import { Spinner3 } from '@/components/icons/spinner'

import { destinationToSlug } from './offers/helpers'

type LangKey = 'en' | 'bs' | 'hr' | 'sr'

export interface TopCountry extends Country {
  volumeGb: number
  validityDays: number
  priceUSD: number
  priceBAM: number
  priceEUR: number
  label?: Partial<Record<LangKey, string>>
}

// Hardcoded catalog prices for the cheapest 5 GB plan on each destination.
const topCountries: Array<TopCountry> = [
  {
    code: 'tr',
    name: '',
    en: 'Turkiye',
    bs: 'Turska',
    hr: 'Turska',
    sr: 'Turska',
    volumeGb: 5,
    validityDays: 7,
    priceUSD: 5.2,
    priceBAM: 8.7,
    priceEUR: 4.5,
  },
  {
    code: 'Europe',
    name: '',
    en: 'Europe',
    bs: 'Europa',
    hr: 'Europa',
    sr: 'Evropa',
    volumeGb: 5,
    validityDays: 7,
    priceUSD: 8.2,
    priceBAM: 13.7,
    priceEUR: 7.1,
  },
  {
    code: 'hr',
    name: '',
    en: 'Croatia',
    bs: 'Hrvatska',
    hr: 'Hrvatska',
    sr: 'Hrvatska',
    volumeGb: 5,
    validityDays: 7,
    priceUSD: 8,
    priceBAM: 13.3,
    priceEUR: 6.8,
  },
  {
    code: 'es',
    name: '',
    en: 'Spain',
    bs: 'Španija',
    hr: 'Španija',
    sr: 'Španija',
    volumeGb: 5,
    validityDays: 7,
    priceUSD: 7.7,
    priceBAM: 12.8,
    priceEUR: 6.6,
  },
  {
    code: 'it',
    name: '',
    en: 'Italy',
    bs: 'Italija',
    hr: 'Italija',
    sr: 'Italija',
    volumeGb: 5,
    validityDays: 5,
    priceUSD: 7.5,
    priceBAM: 12.5,
    priceEUR: 6.4,
  },
  {
    code: 'fr',
    name: '',
    en: 'France',
    bs: 'Francuska',
    hr: 'Francuska',
    sr: 'Francuska',
    volumeGb: 5,
    validityDays: 10,
    priceUSD: 7.7,
    priceBAM: 12.9,
    priceEUR: 6.6,
  },
  {
    code: 'sa',
    name: '',
    en: 'Saudi Arabia',
    bs: 'Saudijska Arabija',
    hr: 'Saudijska Arabija',
    sr: 'Saudijska Arabija',
    volumeGb: 5,
    validityDays: 14,
    priceUSD: 17.9,
    priceBAM: 30,
    priceEUR: 15.4,
  },
  {
    code: 'eg',
    name: '',
    en: 'Egypt',
    bs: 'Egipat',
    hr: 'Egipat',
    sr: 'Egipat',
    volumeGb: 5,
    validityDays: 7,
    priceUSD: 10.6,
    priceBAM: 17.8,
    priceEUR: 9.2,
  },
]

const formatPrice = (value: number, language: string) => {
  const [whole, fraction] = value.toFixed(2).split('.')
  return `${whole}${language === 'en' ? '.' : ','}${fraction}`
}

const TopCountries = () => {
  const { t, i18n } = useTranslation()
  const lang = (i18n.language as LangKey) || 'bs'
  const [currency, setCurrency] = useState<Currency>(lang === 'bs' ? 'BAM' : 'EUR')
  const [pendingCode, setPendingCode] = useState<string | null>(null)

  useEffect(() => {
    const stored = Cookies.get('NEXT_CURRENCY')
    const next = stored || (i18n.language === 'bs' ? 'BAM' : 'EUR')
    setCurrency(next as Currency)
  }, [i18n.language])

  return (
    <section className='bg-white-50 py-8' aria-labelledby='popular-destinations-heading' data-track-section='top_countries'>
      {pendingCode && (
        <div className='fixed inset-0 z-20 flex items-center justify-center bg-white/70'>
          <Spinner3 className='h-10 w-10' />
        </div>
      )}
      <div className='mx-auto max-w-5xl px-4'>
        <div className='text-left md:text-center'>
          <p className='text-[13px] font-semibold uppercase tracking-[0.16em] text-[#F3603C]'>{t('Landing.Popular.Eyebrow')}</p>
          <h2 id='popular-destinations-heading' className='mt-3 text-3xl font-bold tracking-tight text-blue-950'>
            {t('Landing.Popular.Heading')}
          </h2>
          <p className='mt-4 max-w-xl text-lg leading-relaxed text-slate-500 md:mx-auto'>{t('Landing.Popular.Description')}</p>
        </div>

        <div className='mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4'>
          {topCountries.map(item => {
            const name = (item[lang] as string) || item.en
            const label = item.label?.[lang] || name
            const amount = item[`price${currency}`] as number
            const price = `${formatPrice(amount, i18n.language)} ${currency}`
            return (
              <Link
                key={item.code}
                href={`/${i18n.language}/offers/${destinationToSlug(name)}`}
                prefetch={false}
                onClick={() => setPendingCode(item.code)}
                className='flex flex-col rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition-shadow hover:border-blue-200 hover:shadow-md'
              >
                <div className='flex items-center gap-2.5'>
                  <div className='flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg [&_svg]:size-10'>
                    <CountryFlag country={item} className='!size-10 rounded-lg ![background-size:cover]' />
                  </div>
                  <div className='min-w-0'>
                    <h3 className='truncate text-[15px] font-bold leading-tight text-gray-900'>
                      {item.volumeGb} GB {t('OfferCard.Text.ValidityDays', { count: item.validityDays })}
                    </h3>
                    <p className='mt-0.5 truncate text-sm text-gray-500'>{label}</p>
                  </div>
                </div>
                <div className='mt-3 flex items-center justify-between gap-1'>
                  <p className='text-base font-bold text-blue-600'>{price}</p>
                  <span className='flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600'>
                    <ChevronRightIcon className='size-4' aria-hidden />
                  </span>
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default TopCountries
